import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  ArrowLeft, Plus, Trash2, Save, Type, Image, Square,
  List, ChevronUp, ChevronDown, Upload, Loader2, Wand2,
  Copy, Monitor, X, FileText, Columns, Quote, LayoutGrid, Sparkles
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRANSITIONS = [
  { value: 'fade', label: 'Fade' },
  { value: 'zoom', label: 'Zoom' },
  { value: 'slide-left', label: 'Slide Left' },
  { value: 'slide-right', label: 'Slide Right' },
  { value: 'slide-up', label: 'Slide Up' },
  { value: 'flip', label: '3D Flip' },
  { value: 'cube', label: '3D Cube' },
  { value: 'blur', label: 'Blur' },
  { value: 'parallax', label: 'Parallax' },
  { value: 'morph', label: 'Morph' },
  { value: 'none', label: 'None' },
];

export default function SlideEditor({ presId, onBack, onPresent }) {
  const [pres, setPres] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [activeElement, setActiveElement] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [genText, setGenText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showImageSuggest, setShowImageSuggest] = useState(false);
  const [suggestedImages, setSuggestedImages] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestQuery, setSuggestQuery] = useState('');
  const canvasRef = useRef(null);

  useEffect(() => { loadPres(); }, [presId]);

  const loadPres = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/presentation/${presId}`);
      setPres(res.data);
      if (res.data.slides?.length) setActiveSlide(0);
    } catch (err) { alert('Failed to load'); onBack?.(); }
    finally { setLoading(false); }
  };

  const savePres = async () => {
    if (!pres) return;
    setSaving(true);
    try {
      await axios.put(`${API}/presentation/${presId}`, {
        title: pres.title, description: pres.description, theme: pres.theme, slides: pres.slides,
      });
    } catch (err) { alert('Failed to save'); }
    finally { setSaving(false); }
  };

  const addSlide = (layout = 'title-content') => {
    const theme = pres.theme || {};
    const newSlide = {
      id: crypto.randomUUID(), order: (pres.slides || []).length,
      transition: 'fade', layout,
      background: { type: 'theme', value: theme.bg || '#1B3A6B' },
      elements: [], notes: '',
    };
    // Add default elements based on layout
    if (layout === 'title') {
      newSlide.elements = [
        { id: crypto.randomUUID(), type: 'text', x: 10, y: 30, width: 80, height: 20, content: 'Title Here', style: { fontSize: 48, fontWeight: 'bold', textAlign: 'center', color: theme.text || '#fff' } },
        { id: crypto.randomUUID(), type: 'text', x: 15, y: 55, width: 70, height: 10, content: 'Subtitle', style: { fontSize: 24, textAlign: 'center', color: theme.secondary || '#aaa' } },
      ];
    } else if (layout === 'title-content') {
      newSlide.elements = [
        { id: crypto.randomUUID(), type: 'text', x: 5, y: 5, width: 90, height: 12, content: 'Slide Title', style: { fontSize: 36, fontWeight: 'bold', color: theme.text || '#fff' } },
        { id: crypto.randomUUID(), type: 'text', x: 5, y: 22, width: 90, height: 70, content: 'Content goes here...', style: { fontSize: 20, color: theme.text || '#fff' } },
      ];
    } else if (layout === 'two-column') {
      newSlide.elements = [
        { id: crypto.randomUUID(), type: 'text', x: 5, y: 5, width: 90, height: 12, content: 'Title', style: { fontSize: 36, fontWeight: 'bold', color: theme.text || '#fff' } },
        { id: crypto.randomUUID(), type: 'text', x: 5, y: 22, width: 43, height: 70, content: 'Left column', style: { fontSize: 18, color: theme.text || '#fff' } },
        { id: crypto.randomUUID(), type: 'text', x: 52, y: 22, width: 43, height: 70, content: 'Right column', style: { fontSize: 18, color: theme.text || '#fff' } },
      ];
    } else if (layout === 'quote') {
      newSlide.elements = [
        { id: crypto.randomUUID(), type: 'text', x: 10, y: 25, width: 80, height: 40, content: '"Your quote here"', style: { fontSize: 32, fontStyle: 'italic', textAlign: 'center', color: theme.text || '#fff' } },
        { id: crypto.randomUUID(), type: 'text', x: 25, y: 70, width: 50, height: 10, content: '— Author', style: { fontSize: 20, textAlign: 'center', color: theme.accent || '#B8860B' } },
      ];
    }
    setPres({ ...pres, slides: [...(pres.slides || []), newSlide] });
    setActiveSlide((pres.slides || []).length);
    setActiveElement(null);
  };

  const deleteSlide = (idx) => {
    const slides = pres.slides.filter((_, i) => i !== idx);
    setPres({ ...pres, slides });
    if (activeSlide >= slides.length) setActiveSlide(Math.max(0, slides.length - 1));
    setActiveElement(null);
  };

  const moveSlide = (idx, dir) => {
    const slides = [...pres.slides];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= slides.length) return;
    [slides[idx], slides[newIdx]] = [slides[newIdx], slides[idx]];
    setPres({ ...pres, slides });
    setActiveSlide(newIdx);
  };

  const updateSlide = (idx, updates) => {
    const slides = [...pres.slides];
    slides[idx] = { ...slides[idx], ...updates };
    setPres({ ...pres, slides });
  };

  const addElement = (type) => {
    const theme = pres.theme || {};
    const slide = pres.slides[activeSlide];
    if (!slide) return;
    let elem;
    if (type === 'text') {
      elem = { id: crypto.randomUUID(), type: 'text', x: 10, y: 40, width: 40, height: 15, content: 'New text', style: { fontSize: 20, color: theme.text || '#fff' } };
    } else if (type === 'image') {
      elem = { id: crypto.randomUUID(), type: 'image', x: 30, y: 20, width: 40, height: 50, content: '', style: {} };
    } else if (type === 'shape') {
      elem = { id: crypto.randomUUID(), type: 'shape', x: 35, y: 35, width: 30, height: 30, content: '', style: { backgroundColor: theme.accent || '#B8860B', borderRadius: '8px' } };
    }
    if (!elem) return;
    const elements = [...(slide.elements || []), elem];
    updateSlide(activeSlide, { elements });
    setActiveElement(elem.id);
  };

  const updateElement = (elemId, updates) => {
    const slide = pres.slides[activeSlide];
    const elements = slide.elements.map(e => e.id === elemId ? { ...e, ...updates } : e);
    updateSlide(activeSlide, { elements });
  };

  const deleteElement = (elemId) => {
    const slide = pres.slides[activeSlide];
    const elements = slide.elements.filter(e => e.id !== elemId);
    updateSlide(activeSlide, { elements });
    setActiveElement(null);
  };

  const handleCanvasMouseDown = (e, elem) => {
    e.stopPropagation();
    setActiveElement(elem.id);
    const rect = canvasRef.current.getBoundingClientRect();
    setDragging({ elemId: elem.id, startX: e.clientX, startY: e.clientY, origX: elem.x, origY: elem.y, canvasW: rect.width, canvasH: rect.height });
  };

  const handleCanvasMouseMove = (e) => {
    if (!dragging) return;
    const dx = ((e.clientX - dragging.startX) / dragging.canvasW) * 100;
    const dy = ((e.clientY - dragging.startY) / dragging.canvasH) * 100;
    updateElement(dragging.elemId, { x: Math.max(0, dragging.origX + dx), y: Math.max(0, dragging.origY + dy) });
  };

  const handleCanvasMouseUp = () => setDragging(null);

  const handleGenerate = async () => {
    if (!genText.trim()) return;
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/presentation/${presId}/generate`, { text: genText });
      setShowGenerate(false);
      setGenText('');
      loadPres();
      alert(res.data.message);
    } catch (err) { alert(err.response?.data?.detail || 'Generation failed'); }
    finally { setGenerating(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/presentation/${presId}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      loadPres();
      alert(res.data.message);
    } catch (err) { alert(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const suggestImages = async () => {
    const slide = pres.slides?.[activeSlide];
    if (!slide) return;
    const text = slide.elements?.map(e => e.content).filter(Boolean).join(' ') || pres.title;
    if (!text.trim()) return;
    setSuggestLoading(true);
    setShowImageSuggest(true);
    try {
      const res = await axios.post(`${API}/presentation/suggest-images`, { text });
      setSuggestedImages(res.data.images || []);
      setSuggestQuery(res.data.query || '');
    } catch (err) { alert('Failed to suggest images'); }
    finally { setSuggestLoading(false); }
  };

  const insertSuggestedImage = (url) => {
    const theme = pres.theme || {};
    const slide = pres.slides[activeSlide];
    if (!slide) return;
    const elem = { id: crypto.randomUUID(), type: 'image', x: 55, y: 15, width: 40, height: 65, content: url, style: {} };
    const elements = [...(slide.elements || []), elem];
    updateSlide(activeSlide, { elements });
    setActiveElement(elem.id);
    setShowImageSuggest(false);
  };


  if (loading) return <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  if (!pres) return null;

  const currentSlide = pres.slides?.[activeSlide];
  const selectedElem = currentSlide?.elements?.find(e => e.id === activeElement);

  return (
    <div className="flex flex-col h-full" data-testid="slide-editor">
      {/* Top Bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-[#E2DFD8]">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-2 hover:bg-[#F4F2EC] rounded-lg"><ArrowLeft className="w-5 h-5 text-[#475569]" /></button>
          <input type="text" value={pres.title} onChange={e => setPres({...pres, title: e.target.value})} className="text-lg font-bold bg-transparent border-none focus:ring-0 p-0" data-testid="pres-editor-title" />
        </div>
        <div className="flex items-center gap-2">
          <label className="cursor-pointer px-3 py-1.5 border border-[#E2DFD8] rounded-lg text-xs text-[#475569] hover:bg-[#F4F2EC] flex items-center gap-1.5">
            <Upload className="w-3.5 h-3.5" />{uploading ? 'Uploading...' : 'Import'}
            <input type="file" accept=".pptx,.docx,.pdf,.txt" onChange={handleUpload} className="hidden" />
          </label>
          <button onClick={() => setShowGenerate(true)} className="px-3 py-1.5 border border-[#B8860B] text-[#B8860B] rounded-lg text-xs hover:bg-[#B8860B]/10 flex items-center gap-1.5" data-testid="ai-generate-btn">
            <Wand2 className="w-3.5 h-3.5" /> AI Generate
          </button>
          <button onClick={savePres} disabled={saving} className="px-3 py-1.5 bg-[#1B3A6B] text-white rounded-lg text-xs font-medium flex items-center gap-1.5 disabled:opacity-50" data-testid="pres-save-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
          </button>
          {(pres.slides || []).length > 0 && (
            <button onClick={() => onPresent?.(presId)} className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium flex items-center gap-1.5" data-testid="present-from-editor">
              <Monitor className="w-3.5 h-3.5" /> Present
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Slide Thumbnails */}
        <div className="w-48 bg-[#F9F8F6] border-r border-[#E2DFD8] overflow-y-auto p-2 space-y-2 flex-shrink-0">
          {(pres.slides || []).map((s, i) => (
            <div key={s.id} onClick={() => { setActiveSlide(i); setActiveElement(null); }}
              className={`relative rounded-lg border-2 cursor-pointer overflow-hidden group transition-all ${activeSlide === i ? 'border-[#1B3A6B] shadow-md' : 'border-transparent hover:border-[#94A3B8]'}`}
              data-testid={`slide-thumb-${i}`}
            >
              <div className="aspect-video p-2 flex items-center justify-center text-[6px]" style={{ backgroundColor: s.background?.value || '#1B3A6B' }}>
                <span className="text-white/80 text-center truncate text-[8px]">{s.elements?.[0]?.content?.substring(0, 30) || `Slide ${i+1}`}</span>
              </div>
              <span className="absolute bottom-1 left-1 text-[9px] bg-black/40 text-white px-1 rounded">{i+1}</span>
              <div className="absolute top-1 right-1 hidden group-hover:flex gap-0.5">
                <button onClick={e => { e.stopPropagation(); moveSlide(i, -1); }} className="w-4 h-4 bg-white/80 rounded flex items-center justify-center"><ChevronUp className="w-3 h-3" /></button>
                <button onClick={e => { e.stopPropagation(); moveSlide(i, 1); }} className="w-4 h-4 bg-white/80 rounded flex items-center justify-center"><ChevronDown className="w-3 h-3" /></button>
                <button onClick={e => { e.stopPropagation(); deleteSlide(i); }} className="w-4 h-4 bg-red-500 text-white rounded flex items-center justify-center"><X className="w-3 h-3" /></button>
              </div>
            </div>
          ))}

          {/* Add Slide Buttons */}
          <div className="space-y-1 pt-2 border-t border-[#E2DFD8]">
            {[
              { layout: 'title', icon: Type, label: 'Title' },
              { layout: 'title-content', icon: FileText, label: 'Content' },
              { layout: 'two-column', icon: Columns, label: '2 Column' },
              { layout: 'quote', icon: Quote, label: 'Quote' },
              { layout: 'blank', icon: LayoutGrid, label: 'Blank' },
            ].map(l => (
              <button key={l.layout} onClick={() => addSlide(l.layout)} className="w-full py-1.5 px-2 text-[10px] text-[#475569] hover:bg-white rounded flex items-center gap-1.5" data-testid={`add-slide-${l.layout}`}>
                <l.icon className="w-3 h-3" /> {l.label}
              </button>
            ))}
          </div>
        </div>

        {/* Main Canvas */}
        <div className="flex-1 flex flex-col bg-[#E2DFD8] overflow-hidden">
          {currentSlide ? (
            <div className="flex-1 flex items-center justify-center p-4">
              <div ref={canvasRef} className="relative w-full max-w-4xl aspect-video rounded-lg shadow-2xl overflow-hidden cursor-crosshair" style={{ backgroundColor: currentSlide.background?.value || '#1B3A6B' }}
                onMouseMove={handleCanvasMouseMove} onMouseUp={handleCanvasMouseUp} onMouseLeave={handleCanvasMouseUp}
                onClick={() => setActiveElement(null)}
              >
                {(currentSlide.elements || []).map(elem => (
                  <div key={elem.id}
                    className={`absolute cursor-move group ${activeElement === elem.id ? 'ring-2 ring-[#B8860B] ring-offset-1' : 'hover:ring-1 hover:ring-white/30'}`}
                    style={{ left: `${elem.x}%`, top: `${elem.y}%`, width: `${elem.width}%`, height: `${elem.height}%` }}
                    onMouseDown={e => handleCanvasMouseDown(e, elem)}
                    onClick={e => { e.stopPropagation(); setActiveElement(elem.id); }}
                  >
                    {elem.type === 'text' && (
                      <div className="w-full h-full overflow-hidden whitespace-pre-wrap" style={{
                        fontSize: `${(elem.style?.fontSize || 20) * 0.5}px`,
                        fontWeight: elem.style?.fontWeight || 'normal',
                        fontStyle: elem.style?.fontStyle || 'normal',
                        textAlign: elem.style?.textAlign || 'left',
                        color: elem.style?.color || '#fff',
                        lineHeight: elem.style?.lineHeight || 1.4,
                      }}>
                        {elem.content}
                      </div>
                    )}
                    {elem.type === 'image' && (
                      elem.content ? <img src={elem.content} alt="" className="w-full h-full object-contain" />
                      : <div className="w-full h-full border-2 border-dashed border-white/30 flex items-center justify-center"><Image className="w-6 h-6 text-white/30" /></div>
                    )}
                    {elem.type === 'shape' && (
                      <div className="w-full h-full" style={{ backgroundColor: elem.style?.backgroundColor || '#B8860B', borderRadius: elem.style?.borderRadius || '0' }} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Monitor className="w-12 h-12 text-[#94A3B8] mx-auto mb-3" />
                <p className="text-[#475569]">Add a slide to get started</p>
              </div>
            </div>
          )}

          {/* Element Toolbar */}
          {currentSlide && (
            <div className="bg-white border-t border-[#E2DFD8] px-4 py-2 flex items-center gap-2">
              <span className="text-xs text-[#94A3B8] mr-2">Add:</span>
              <button onClick={() => addElement('text')} className="px-2 py-1 border border-[#E2DFD8] rounded text-xs flex items-center gap-1 hover:bg-[#F4F2EC]"><Type className="w-3 h-3" /> Text</button>
              <button onClick={() => addElement('image')} className="px-2 py-1 border border-[#E2DFD8] rounded text-xs flex items-center gap-1 hover:bg-[#F4F2EC]"><Image className="w-3 h-3" /> Image</button>
              <button onClick={() => addElement('shape')} className="px-2 py-1 border border-[#E2DFD8] rounded text-xs flex items-center gap-1 hover:bg-[#F4F2EC]"><Square className="w-3 h-3" /> Shape</button>
              <button onClick={suggestImages} className="px-2 py-1 bg-[#B8860B]/10 border border-[#B8860B]/30 text-[#B8860B] rounded text-xs flex items-center gap-1 hover:bg-[#B8860B]/20" data-testid="suggest-image-btn">
                <Sparkles className="w-3 h-3" /> Suggest Image
              </button>
              <div className="ml-auto flex items-center gap-2">
                <span className="text-xs text-[#94A3B8]">Transition:</span>
                <select value={currentSlide.transition || 'fade'} onChange={e => updateSlide(activeSlide, { transition: e.target.value })} className="text-xs px-2 py-1 border border-[#E2DFD8] rounded-lg" data-testid="slide-transition">
                  {TRANSITIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Properties Panel */}
        <div className="w-56 bg-white border-l border-[#E2DFD8] overflow-y-auto p-3 flex-shrink-0">
          {selectedElem ? (
            <div className="space-y-3" data-testid="element-props">
              <h4 className="text-xs font-bold text-[#0F1A2A] uppercase tracking-wider">Element Properties</h4>
              {selectedElem.type === 'text' && (
                <div className="space-y-2">
                  <textarea value={selectedElem.content} onChange={e => updateElement(selectedElem.id, { content: e.target.value })} rows={4} className="w-full px-2 py-1.5 border border-[#E2DFD8] rounded text-xs resize-none" />
                  <div className="grid grid-cols-2 gap-1.5">
                    <div>
                      <label className="text-[9px] text-[#94A3B8]">Size</label>
                      <input type="number" value={selectedElem.style?.fontSize || 20} onChange={e => updateElement(selectedElem.id, { style: { ...selectedElem.style, fontSize: parseInt(e.target.value) || 20 } })} className="w-full px-1.5 py-1 border border-[#E2DFD8] rounded text-xs" />
                    </div>
                    <div>
                      <label className="text-[9px] text-[#94A3B8]">Color</label>
                      <input type="color" value={selectedElem.style?.color || '#ffffff'} onChange={e => updateElement(selectedElem.id, { style: { ...selectedElem.style, color: e.target.value } })} className="w-full h-7 rounded cursor-pointer" />
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {['left', 'center', 'right'].map(a => (
                      <button key={a} onClick={() => updateElement(selectedElem.id, { style: { ...selectedElem.style, textAlign: a } })} className={`flex-1 py-1 text-[9px] rounded ${selectedElem.style?.textAlign === a ? 'bg-[#1B3A6B] text-white' : 'bg-[#F4F2EC]'}`}>{a}</button>
                    ))}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => updateElement(selectedElem.id, { style: { ...selectedElem.style, fontWeight: selectedElem.style?.fontWeight === 'bold' ? 'normal' : 'bold' } })} className={`flex-1 py-1 text-xs font-bold rounded ${selectedElem.style?.fontWeight === 'bold' ? 'bg-[#1B3A6B] text-white' : 'bg-[#F4F2EC]'}`}>B</button>
                    <button onClick={() => updateElement(selectedElem.id, { style: { ...selectedElem.style, fontStyle: selectedElem.style?.fontStyle === 'italic' ? 'normal' : 'italic' } })} className={`flex-1 py-1 text-xs italic rounded ${selectedElem.style?.fontStyle === 'italic' ? 'bg-[#1B3A6B] text-white' : 'bg-[#F4F2EC]'}`}>I</button>
                  </div>
                </div>
              )}
              {selectedElem.type === 'image' && (
                <div>
                  <label className="text-[9px] text-[#94A3B8]">Image URL</label>
                  <input type="url" value={selectedElem.content || ''} onChange={e => updateElement(selectedElem.id, { content: e.target.value })} placeholder="https://..." className="w-full px-2 py-1.5 border border-[#E2DFD8] rounded text-xs" />
                </div>
              )}
              {selectedElem.type === 'shape' && (
                <div className="space-y-2">
                  <div>
                    <label className="text-[9px] text-[#94A3B8]">Color</label>
                    <input type="color" value={selectedElem.style?.backgroundColor || '#B8860B'} onChange={e => updateElement(selectedElem.id, { style: { ...selectedElem.style, backgroundColor: e.target.value } })} className="w-full h-7 rounded cursor-pointer" />
                  </div>
                  <div>
                    <label className="text-[9px] text-[#94A3B8]">Radius</label>
                    <select value={selectedElem.style?.borderRadius || '0'} onChange={e => updateElement(selectedElem.id, { style: { ...selectedElem.style, borderRadius: e.target.value } })} className="w-full px-1.5 py-1 border border-[#E2DFD8] rounded text-xs">
                      <option value="0">Square</option>
                      <option value="8px">Rounded</option>
                      <option value="50%">Circle</option>
                    </select>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-1.5">
                <div><label className="text-[9px] text-[#94A3B8]">X %</label><input type="number" value={Math.round(selectedElem.x)} onChange={e => updateElement(selectedElem.id, { x: parseFloat(e.target.value) || 0 })} className="w-full px-1.5 py-1 border border-[#E2DFD8] rounded text-xs" /></div>
                <div><label className="text-[9px] text-[#94A3B8]">Y %</label><input type="number" value={Math.round(selectedElem.y)} onChange={e => updateElement(selectedElem.id, { y: parseFloat(e.target.value) || 0 })} className="w-full px-1.5 py-1 border border-[#E2DFD8] rounded text-xs" /></div>
                <div><label className="text-[9px] text-[#94A3B8]">W %</label><input type="number" value={Math.round(selectedElem.width)} onChange={e => updateElement(selectedElem.id, { width: parseFloat(e.target.value) || 10 })} className="w-full px-1.5 py-1 border border-[#E2DFD8] rounded text-xs" /></div>
                <div><label className="text-[9px] text-[#94A3B8]">H %</label><input type="number" value={Math.round(selectedElem.height)} onChange={e => updateElement(selectedElem.id, { height: parseFloat(e.target.value) || 10 })} className="w-full px-1.5 py-1 border border-[#E2DFD8] rounded text-xs" /></div>
              </div>
              <button onClick={() => deleteElement(selectedElem.id)} className="w-full py-1.5 bg-red-50 text-red-600 rounded text-xs hover:bg-red-100"><Trash2 className="w-3 h-3 inline mr-1" /> Delete Element</button>
            </div>
          ) : currentSlide ? (
            <div className="space-y-3">
              <h4 className="text-xs font-bold text-[#0F1A2A] uppercase tracking-wider">Slide Settings</h4>
              <div>
                <label className="text-[9px] text-[#94A3B8]">Background</label>
                <input type="color" value={currentSlide.background?.value || '#1B3A6B'} onChange={e => updateSlide(activeSlide, { background: { type: 'solid', value: e.target.value } })} className="w-full h-8 rounded cursor-pointer" />
              </div>
              <div>
                <label className="text-[9px] text-[#94A3B8]">Speaker Notes</label>
                <textarea value={currentSlide.notes || ''} onChange={e => updateSlide(activeSlide, { notes: e.target.value })} rows={3} placeholder="Notes..." className="w-full px-2 py-1.5 border border-[#E2DFD8] rounded text-xs resize-none" />
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Image Suggestion Panel */}
      {showImageSuggest && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowImageSuggest(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-6 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="image-suggest-panel">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-[#0F1A2A] flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#B8860B]" /> Suggested Images</h3>
                {suggestQuery && <p className="text-xs text-[#94A3B8] mt-0.5">Search: &ldquo;{suggestQuery}&rdquo;</p>}
              </div>
              <button onClick={() => setShowImageSuggest(false)} className="p-1 hover:bg-[#F4F2EC] rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            {suggestLoading ? (
              <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-[#B8860B]" /><p className="text-sm text-[#475569]">Finding images...</p></div>
            ) : suggestedImages.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {suggestedImages.map((img, i) => (
                  <button key={i} onClick={() => insertSuggestedImage(img.url)} className="group relative rounded-xl overflow-hidden border-2 border-transparent hover:border-[#B8860B] transition-all" data-testid={`suggest-img-${i}`}>
                    <img src={img.thumb || img.url} alt={img.description} className="w-full aspect-video object-cover" loading="lazy" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                      <span className="text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">Insert</span>
                    </div>
                    {img.author && <p className="absolute bottom-0 left-0 right-0 text-[8px] text-white/60 bg-black/30 px-1 py-0.5 truncate">{img.author}</p>}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-center text-[#94A3B8] py-8">No images found. Try editing your slide content.</p>
            )}
          </div>
        </div>
      )}

      {/* AI Generate Modal */}
      {showGenerate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowGenerate(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6" onClick={e => e.stopPropagation()} data-testid="ai-generate-modal">
            <h3 className="text-lg font-bold text-[#0F1A2A] mb-4 flex items-center gap-2"><Wand2 className="w-5 h-5 text-[#B8860B]" /> AI Generate Slides</h3>
            <textarea value={genText} onChange={e => setGenText(e.target.value)} rows={8} placeholder="Paste your content, outline, or topic. AI will generate professional slides with transitions..." className="w-full px-4 py-3 border border-[#E2DFD8] rounded-lg focus:ring-2 focus:ring-[#B8860B] resize-none mb-4" data-testid="ai-gen-text" />
            <div className="flex gap-2">
              <button onClick={() => setShowGenerate(false)} className="flex-1 py-2.5 border border-[#E2DFD8] rounded-lg">Cancel</button>
              <button onClick={handleGenerate} disabled={generating || !genText.trim()} className="flex-1 py-2.5 bg-[#B8860B] text-white rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50" data-testid="ai-gen-submit">
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                {generating ? 'Generating...' : 'Generate Slides'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
